import React, { FC, useEffect, useMemo } from 'react';
import { getShortLanguageCode } from 'utils/format';
import { Autocomplete, debounce, TextField } from '@mui/material';
import { GoogleAddressAutoComplete, AddressFormDetails } from 'models/contact';
import { useTranslation } from 'react-i18next';
import { addressAutoCompleteSearch, addressDetails } from 'utils/google-places';
import { Country } from 'models/country';

type AddressAutocompleteProps = {
    onChange: (selectedAddress: AddressFormDetails) => void;
    country: Country;
};
const AddressAutocomplete: FC<AddressAutocompleteProps> = ({ onChange, country }: AddressAutocompleteProps) => {
    const { t: translate } = useTranslation(['common', 'organization']);
    const [selectedAddress, setSelectedAddress] = React.useState<GoogleAddressAutoComplete | null>(null);
    const [selectedAddressDetails, setSelectedAddressDetails] = React.useState<AddressFormDetails>();
    const [inputValue, setInputValue] = React.useState('');
    const [options, setOptions] = React.useState<GoogleAddressAutoComplete[]>([]);
    const [fieldTouched, setFieldTouched] = React.useState(false);
    const [selectedCountry, setSelectedCountry] = React.useState<Country>(country);
    const language = getShortLanguageCode();

    const search = useMemo(
        () =>
            debounce(async (input: string, selectedCountry: Country) => {
                const results = await addressAutoCompleteSearch(input, selectedCountry.alpha2Code, language);
                setOptions(results);
            }, 300),
        [],
    );

    useEffect(() => {
        if (inputValue === '') {
            setOptions(selectedAddress ? [selectedAddress] : []);
            return undefined;
        }
        search(inputValue, selectedCountry);
    }, [selectedAddress, inputValue, search, selectedCountry]);

    useEffect(() => {
        setSelectedCountry(country);
    }, [country]);

    return (
        <Autocomplete
            fullWidth
            getOptionLabel={(option) => (typeof option === 'string' ? option : option.text)}
            filterOptions={(x) => x}
            options={options}
            autoComplete
            includeInputInList
            filterSelectedOptions
            value={selectedAddress}
            noOptionsText={translate('organization:NO_ADDRESS_OPTIONS')}
            onChange={(_, newValue: GoogleAddressAutoComplete | null) => {
                setOptions(newValue ? [newValue, ...options] : options);
                setSelectedAddress(newValue);
                if (newValue) {
                    addressDetails(newValue.googleId).then((addressDetails) => {
                        setSelectedAddressDetails(addressDetails);
                        onChange(addressDetails);
                        setFieldTouched(true);
                    });
                }
            }}
            onInputChange={(_, newInputValue) => {
                setInputValue(newInputValue);
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={translate('organization:SEARCH_ADDRESS')}
                    fullWidth
                    helperText={
                        !selectedAddressDetails?.isCompleteAddress &&
                        fieldTouched &&
                        translate('organization:VALIDATION:MISSING_STREET_NUMBER')
                    }
                    error={!selectedAddressDetails?.isCompleteAddress && fieldTouched}
                />
            )}
            isOptionEqualToValue={(option, value) => option.googleId === value.googleId}
        />
    );
};

export default AddressAutocomplete;
